/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  ApolloCache,
  DocumentNode,
  NormalizedCacheObject,
} from '@apollo/client';
import { CardChecklistDueDocument } from './CardChecklistDueQuery.generated';
import { CardChecklistAllDocument } from './CardChecklistAllQuery.generated';
import { Maybe, Card, Checklist } from '../../../generated';
import { firstLetterToUpper } from '../../stringOperations';

type ChecklistFilter = 'due' | 'all';

type SupportedParent = Card;

export type GeneralizedCardChecklistsQuery<T extends SupportedParent> = {
  __typename: 'Query';
} & {
  [K in Lowercase<T['__typename']>]?: Maybe<
    Pick<T, 'id' | '__typename'> & {
      checklists: Array<
        { __typename: 'Checklist' } & Pick<Checklist, 'id' | 'checkItems'>
      >;
    }
  >;
};

const QUERY_MAP: Record<
  Lowercase<SupportedParent['__typename']>,
  Record<ChecklistFilter, DocumentNode>
> = {
  card: {
    due: CardChecklistDueDocument,
    all: CardChecklistAllDocument,
  },
};

export function removeDeleted(
  cache: ApolloCache<NormalizedCacheObject>,
  type: string,
  id: string,
) {
  cache.evict({ id: `${type}:${id}` });
  cache.gc();
}

function readCardChecklistsFromCache<Parent extends SupportedParent>({
  cache,
  query,
  parentType,
  parentId,
}: {
  cache: ApolloCache<NormalizedCacheObject>;
  query: DocumentNode;
  parentType: Lowercase<Parent['__typename']>;
  parentId: string;
}) {
  return cache.readQuery<GeneralizedCardChecklistsQuery<Parent>>({
    query,
    variables: {
      parentId,
    },
  })?.[parentType]?.checklists;
}

function writeCardChecklistsToCache<Parent extends SupportedParent>({
  cache,
  query,
  parentType,
  parentId,
  checklists,
}: {
  cache: ApolloCache<NormalizedCacheObject>;
  query: DocumentNode;
  parentType: Lowercase<Parent['__typename']>;
  parentId: string;
  checklists: Partial<Checklist>[];
}) {
  return cache.writeQuery({
    query,
    data: {
      __typename: 'Query',
      [`${parentType}`]: {
        id: parentId,
        checklists,
        __typename: firstLetterToUpper(parentType),
      },
    },
    variables: {
      parentId,
    },
  });
}

export function assignChecklistToFilters<Parent extends SupportedParent>({
  addTo,
  removeFrom,
  cache,
  parentType,
  parentIds,
  checklistId,
}: {
  addTo?: ChecklistFilter[];
  removeFrom?: ChecklistFilter[];
  cache: ApolloCache<NormalizedCacheObject>;
  parentType: Lowercase<Parent['__typename']>;
  parentIds: string[];
  checklistId: string;
}) {
  parentIds.forEach((parentId) => {
    if (addTo && addTo.length > 0) {
      addTo.forEach((filter) => {
        addChecklistToCard({
          filter,
          cache,
          parentType,
          parentId,
          checklistId,
        });
      });
    }
    if (removeFrom && removeFrom.length > 0) {
      removeFrom.forEach((filter) => {
        removeChecklistFromCard({
          filter,
          cache,
          parentType,
          parentId,
          checklistId,
        });
      });
    }
  });
}

function removeChecklistFromCard<Parent extends SupportedParent>({
  filter,
  cache,
  parentType,
  parentId,
  checklistId,
}: {
  filter: ChecklistFilter;
  cache: ApolloCache<NormalizedCacheObject>;
  parentType: Lowercase<Parent['__typename']>;
  parentId: string;
  checklistId: string;
}) {
  const query = QUERY_MAP[parentType][filter];
  const params = { cache, query, parentType, parentId };
  const existingChecklistsMatchingFilter = readCardChecklistsFromCache(params);

  const newCardsMatchingFilter = existingChecklistsMatchingFilter?.filter(
    (checklist) => checklist.id !== checklistId,
  );

  writeCardChecklistsToCache({
    ...params,
    checklists: [...(newCardsMatchingFilter ?? [])],
  });
}

function addChecklistToCard<Parent extends SupportedParent>({
  filter,
  cache,
  parentType,
  parentId,
  checklistId,
}: {
  filter: ChecklistFilter;
  cache: ApolloCache<NormalizedCacheObject>;
  parentType: Lowercase<Parent['__typename']>;
  parentId: string;
  checklistId: string;
}) {
  const query = QUERY_MAP[parentType][filter];
  const params = { cache, query, parentType, parentId };
  const existingChecklistsMatchingFilter = readCardChecklistsFromCache(params);

  const newChecklistsMatchingFilter = existingChecklistsMatchingFilter?.filter(
    (checklist) => checklist.id !== checklistId,
  );
  writeCardChecklistsToCache({
    ...params,
    checklists: [
      ...(newChecklistsMatchingFilter ?? []),
      {
        id: checklistId,
        __typename: 'Checklist',
      },
    ],
  });
}
